// Phase 11 · Step 7: the promises in her contract, turned into dated tasks and a weekly-update log.
//   Section 7: the couple sends vendor contracts, the guest count and contacts 60 days out, and names a
//   day-of decision maker; she sends them the vendor meal count before the final catering order.
//   Section 2: Wedding Day Coordination starts six to eight weeks out; Partial and Full-Service promise
//   a written status update every week of the Planning Period.
// Shared by the dashboard and the server. Plain JS, no imports beyond packages.js and fees.js.
import { normalizeSettings, packageById } from "./packages.js";
import { ymd, addDays } from "./fees.js";

const shortDay = day => {
  const [y, m, d] = String(day).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
};

/** Tasks the agreement creates for one couple. Couple-owned ones also show in their portal. */
export function contractTasks({ client, settings }) {
  const s = normalizeSettings(settings);
  const event = ymd(client && client.date);
  if (!event) return [];
  const t = s.terms;
  const due = n => shortDay(addDays(event, -n));
  const task = (key, title, description, daysBefore, forCouple) => ({
    id: (client.id || "c") + "-contract-" + key, title, description, status: "incomplete", due: due(daysBefore),
    assignedTo: forCouple ? "Couple" : "", notes: "", custom: false, source: "contract", owner: forCouple ? "couple" : "planner",
    ...(forCouple ? { coupleTitle: forCouple[0], coupleText: forCouple[1] } : {})
  });
  const list = [
    task("vendor-docs", "Couple sends vendor contracts, guest count and contacts",
      "From the agreement (Client Responsibilities): copies of every vendor contract, the guest count and all contact details, at least 60 days before the wedding.", 60,
      ["Send me your vendor contracts, guest count and contacts", "Copies of every vendor contract, your guest count and everyone's contact details, so I can confirm the day with each of them."]),
    task("decision-maker", "Couple names a day-of decision maker",
      "From the agreement (Client Responsibilities): one person who can make day-of decisions if the couple isn't available.", 60,
      ["Name your day-of decision maker", "One person I can go to for decisions on the day, so you don't have to step away."]),
    task("meal-count", "Send the couple the vendor meal count",
      "From the agreement (Client Responsibilities): before their final catering order is due, a plate count for every vendor working the event start to finish.", t.vendorMealDaysBefore, false)
  ];
  if (client.packageId === "wedding-day") list.push(task("involvement", "My involvement begins",
    "Wedding Day Coordination starts six to eight weeks before the wedding: confirm the details and review their timeline.", 56, false));
  return list;
}

/** Adds the agreement's tasks that aren't there yet (by id). Returns the same array when nothing changes. */
export function mergeContractTasks(existing, add) {
  const list = Array.isArray(existing) ? existing : [];
  const have = new Set(list.map(x => x && x.id));
  const fresh = (add || []).filter(x => !have.has(x.id));
  return fresh.length ? [...list, ...fresh] : list;
}

/** "What I need from you" for the portal: couple-owned agreement tasks, title, due and done only. */
export function coupleNeeds(tasks) {
  return (Array.isArray(tasks) ? tasks : []).filter(x => x && x.source === "contract" && x.owner === "couple")
    .map(x => ({ id: x.id, title: x.coupleTitle || x.title, description: x.coupleText || "", due: x.due || "", done: x.status === "complete" }));
}

/** ISO week key for a YYYY-MM-DD day, e.g. "2026-W43" (weeks start on Monday). */
export function isoWeek(day) {
  const [y, m, d] = String(day).split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  const dow = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dow);
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const w = Math.ceil(((t - y0) / 86400000 + 1) / 7);
  return t.getUTCFullYear() + "-W" + String(w).padStart(2, "0");
}

/**
 * Whether this couple is owed a weekly update this week: their package promises one, the agreement is in
 * effect (executed or signed on paper), and the wedding hasn't passed. Returns null when no update is owed.
 */
export function weeklyUpdate({ client, contract, settings, reports, today }) {
  const s = normalizeSettings(settings);
  const pkg = client && client.packageId ? packageById(s, client.packageId) : null;
  if (!pkg || !pkg.weeklyUpdates) return null;
  if (!contract || !["Executed", "Signed"].includes(contract.status)) return null;
  const event = ymd(client.date);
  if (event && event < today) return null;
  const week = isoWeek(today);
  const sent = reports && reports[week];
  return { week, sent: sent ? sent.sentAt : null, by: sent ? sent.by : null };
}
